import { useEffect, useRef, useState } from "react";
import { api, getToken } from "../utils/helpers";
import { mergeMonitorStatistics, monitorNeedsRetry } from "../utils/monitorSnapshotState";
import useDashboardStore from "../store/useDashboardStore";

// Lists and statistics publish independently; refreshes retain the last successful values.
export default function useSpacesHomeData(spaces, enabled) {
  const optimized = useDashboardStore(s => s.experimentalCaching);
  const [snapshot, setSnapshot] = useState({ instances: {}, stats: {} });
  const [errors, setErrors] = useState({ instances: {}, stats: {} });
  const session = getToken?.();
  const scope = JSON.stringify([session, spaces.map(s => [s.id, s.role, s.permissions])]);
  const previousScope = useRef(scope);
  const sourceBindings = useRef(new Map());

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const current = () => !controller.signal.aborted && getToken?.() === session;
    const options = { signal: controller.signal, retry: false };
    const timers = new Map(), attempts = new Map();
    if (previousScope.current !== scope) {
      previousScope.current = scope; sourceBindings.current.clear();
      setSnapshot({ instances: {}, stats: {} });
    }
    const schedule = (key, task) => {
      if (!current() || timers.has(key)) return;
      const count = attempts.get(key) || 0;
      attempts.set(key, count + 1);
      timers.set(key, setTimeout(() => { timers.delete(key); if (current()) task(); }, Math.min(5000 * 2 ** Math.min(count, 3), 30000)));
    };
    const denied = error => [401, 403].includes(error?.status);
    const retryable = error => !error?.status || error.status === 429 || error.status >= 500;
    setErrors({ instances: {}, stats: {} });
    const recordError = (kind, id, error) => {
      if (current()) setErrors(previous => ({ ...previous, [kind]: { ...previous[kind], [id]: error } }));
    };
    const loadStats = async inst => {
      if (!current()) return;
      try {
        const result = await api(`/api/instances/${inst.id}/stats`, options);
        if (!current()) return;
        const errorsOnly = result?._errorMonitor?.enabled === true;
        const validCount = value => Number.isFinite(value) && value >= 0 || errorsOnly && value === null;
        if (!result || !validCount(result.total) || !validCount(result.failed)) throw Object.assign(new Error("Invalid statistics response"), { code: "INVALID_STATISTICS_RESPONSE" });
        const stats = { failed: result.failed, completed: result.completed, total: result.total, ageMs: result._snapshotAgeMs ?? null,
          ...(errorsOnly ? { _errorMonitor: result._errorMonitor, _source: result._source, _counts: result._counts } : {}) };
        setSnapshot(previous => ({ ...previous, stats: { ...previous.stats, [inst.id]: mergeMonitorStatistics(previous.stats[inst.id], stats) } }));
        setErrors(previous => { const stats = { ...previous.stats }; delete stats[inst.id]; return { ...previous, stats }; });
        if (monitorNeedsRetry(stats)) schedule(`stats:${inst.id}`, () => loadStats(inst));
        else attempts.delete(`stats:${inst.id}`);
      } catch (error) {
        recordError("stats", inst.id, error);
        if (current() && denied(error)) setSnapshot(previous => { const stats = { ...previous.stats }; delete stats[inst.id]; return { ...previous, stats }; });
        else if (retryable(error)) schedule(`stats:${inst.id}`, () => loadStats(inst));
      }
    };
    const loadSpace = async space => {
      try {
        const list = await api(`/api/spaces/${space.id}/instances`, options);
        if (!current()) return;
        if (!Array.isArray(list)) throw Object.assign(new Error("Invalid instances response"), { code: "INVALID_INSTANCES_RESPONSE" });
        for (const inst of list) {
          const binding = JSON.stringify([inst.tenantUrl, inst.serviceKeyVon, inst.policyCredentials, inst.policyInheritanceError]);
          if (sourceBindings.current.has(inst.id) && sourceBindings.current.get(inst.id) !== binding)
            setSnapshot(previous => { const stats = { ...previous.stats }; delete stats[inst.id]; return { ...previous, stats }; });
          sourceBindings.current.set(inst.id, binding);
        }
        setSnapshot(previous => ({ ...previous, instances: { ...previous.instances, [space.id]: list } }));
        setErrors(previous => { const instances = { ...previous.instances }; delete instances[space.id]; return { ...previous, instances }; });
        attempts.delete(`space:${space.id}`);
        if (optimized) await Promise.all(list.map(loadStats));
        else for (const inst of list) await loadStats(inst);
      } catch (error) {
        recordError("instances", space.id, error);
        if (current() && denied(error)) setSnapshot(previous => { const instances = { ...previous.instances }, stats = { ...previous.stats };
          for (const inst of instances[space.id] || []) delete stats[inst.id]; delete instances[space.id]; return { instances, stats }; });
        else if (retryable(error)) schedule(`space:${space.id}`, () => loadSpace(space));
      }
    };
    spaces.forEach(loadSpace);
    return () => { controller.abort(); for (const timer of timers.values()) clearTimeout(timer); };
  }, [spaces, enabled, optimized, scope, session]);

  return { spInstances: snapshot.instances, spInstStats: snapshot.stats, errors };
}
